/**
 * Page helpers for the Help center: the catalog label of the tool on screen,
 * and the page screenshot. The old Jira report-issue types and hook that used
 * to live here went away when the Service Desk replaced Jira; the Help center
 * files tickets through `serviceDeskClient.ts`.
 */

import { toPng } from 'html-to-image';
import type { SdAttachmentInput } from '@ampersandbrands/ui';
import { findTool } from '@/data/tools.config';
import { activeToolForPath } from '@/lib/toolRouting';

/** The auto-captured page screenshot, in the shape the Service Desk takes attachments. */
export type SupportScreenshot = SdAttachmentInput;

export function cockpitToolLabelForPath(pathname: string): string | undefined {
  const active = activeToolForPath(pathname);
  if (!active) return undefined;
  const found = findTool(active.deptId, active.toolId);
  return found ? `${found.dept.name} - ${found.tool.name}` : undefined;
}

function screenshotFilename(tool?: string): string {
  const label = (tool ?? 'cockpit').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${label || 'cockpit'}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
}

export async function captureCockpitScreenshot(tool?: string): Promise<SupportScreenshot | null> {
  const node = document.querySelector<HTMLElement>('[data-cockpit-main]') ?? document.body;
  try {
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      backgroundColor: document.documentElement.classList.contains('dark') ? '#171714' : '#f6f1e7',
    });
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    if (!base64) return null;
    return {
      filename: screenshotFilename(tool),
      content_type: 'image/png',
      base64,
    };
  } catch {
    return null;
  }
}
